import React, { useState, useEffect, useRef } from "react";
import {
  BarChart3, // Dashboard
  Building2, // Proyectos o Constructora Logo
  Wallet, // Cajas
  TrendingUp, // Entradas
  TrendingDown, // Salidas
  FileText, // Facturas
  RefreshCw, // Cambios
  DollarSign, // Cobros
  Scale, // Arqueos
  Settings, // Admin
  LogOut, // Cerrar Sesión
  User, // Usuario
  UserCog, // Configuración de cuenta
  Briefcase, // Honorarios y MO
  ClipboardList, // Presupuestos
  Target, // Presupuesto Inicial
  Calendar, // Cronograma (Gantt)
  X, // Cerrar (en móvil, aunque no se usa en desktop actual)
  ChevronsLeft, // Colapsar/Expandir sidebar
  Menu, // Abrir (en móvil)
  ChevronRight, // Indicador de activo
  PieChart, // Ahora no usado para Presupuesto Inicial, pero se mantiene en imports
  LayoutDashboard, // Alternativa para Dashboard
  CreditCard, // Alternativa para Facturas
  FolderKanban, // Alternativa para Cronograma
  Banknote, // Alternativa para Cajas
  ArrowDownCircle, // Alternativa para Entradas
  ArrowUpCircle, // Alternativa para Salidas
  GitPullRequest, // Alternativa para Cambios
  Receive, // Alternativa para Cobros
  GanttChart, // Alternativa para Arqueos
  Users, // ✅ NUEVO: Para KPI Trabajadores
  Crown, // ✅ NUEVO: Para vistas de super admin
  Database, // ✅ NUEVO: Para estadísticas
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ModalConfiguracionCuenta from "./ModalConfiguracionCuenta";

// ✅ NUEVA IMPORTACIÓN: esSuperAdmin
import { puedeVerKPITrabajadores, esSuperAdmin } from "./permisos";

// Esta función es la que está en permisos.js y se asume que está importada
// o que se mantiene consistente aquí para la lógica de acceso a vistas.
const puedeAccederAVista = (permisosClienteObj, vistaId) => {
  // ✅ CONTROL ESPECIAL: KPI Trabajadores
  if (vistaId === "kpi-trabajadores") {
    return false; // Los clientes NUNCA pueden acceder a KPI Trabajadores
  }

  // ✅ CONTROL ESPECIAL: Vistas de sistema (solo super admins)
  if (vistaId.startsWith("system-")) {
    return false; // Los clientes NUNCA pueden acceder a vistas de sistema
  }

  if (!permisosClienteObj) {
    return vistaId === "honorarios-mo"; // Fallback seguro
  }

  if (
    permisosClienteObj.tipoProyecto === "llave_en_mano" &&
    vistaId === "honorarios-mo"
  ) {
    return true;
  }

  if (
    permisosClienteObj.permisos &&
    permisosClienteObj.permisos.vistas_permitidas &&
    permisosClienteObj.permisos.vistas_permitidas.includes(vistaId)
  ) {
    return true;
  }

  return false;
};

// Componente SidebarMobile (se mantiene como estaba, no se modifica)
function SidebarMobile({
  constructora,
  usuario,
  currentView,
  setCurrentView,
  onLogout,
  permisosCliente,
  onUsuarioUpdate, // Para actualizar el estado del usuario
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const navRefMobile = useRef(null); // Añadido ref para scroll fix en mobile si se desea

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const checkVistaAccess = (vistaId) => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const isConstructoraOrEmpleado =
      usuario?.tipo_usuario === "constructora" ||
      usuario?.tipo_usuario === "empleado";

    // ✅ CONTROL ESPECIAL: Configuración de cuenta (todos los usuarios)
    if (vistaId === "configuracion-cuenta") {
      return true;
    }

    // ✅ CONTROL ESPECIAL: KPI Trabajadores
    if (vistaId === "kpi-trabajadores") {
      return puedeVerKPITrabajadores(usuario);
    }

    // ✅ CONTROL ESPECIAL: Vistas de sistema (solo super admins)
    if (vistaId.startsWith("system-")) {
      return esSuperAdmin(usuario);
    }

    if (isConstructoraOrEmpleado) return true;

    if (isCliente) {
      return puedeAccederAVista(permisosCliente, vistaId);
    }
    return false;
  };

  const getAllMenuItems = () => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const esLlaveEnMano = permisosCliente?.tipoProyecto === "llave_en_mano";
    const esAdministracion =
      permisosCliente?.tipoProyecto === "administracion" ||
      permisosCliente?.tipoProyecto === "administracion_porcentaje";

    // Reordenados según la especificación del usuario
    const items = [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        color: "#3b82f6",
      },
      // Agregar Caja solo para clientes, justo después del Dashboard
      ...(isCliente ? [{
        id: "cajas",
        label: "Caja",
        icon: Wallet,
        color: "#2563eb",
      }] : []),
      {
        id: "honorarios-mo",
        label: isCliente && esLlaveEnMano ? "Resumen de obra" : "Honorarios y MO",
        icon: Briefcase,
        color: "#f59e0b",
      },
      {
        id: "facturas-comprobantes",
        label: "Facturas",
        icon: FileText,
        color: "#84cc16",
      },
      {
        id: "presupuestos",
        label: "Presupuestos",
        icon: ClipboardList,
        color: "#ec4899",
      },
      ...(isCliente ? [] : [{ id: "gantt", label: "Cronograma", icon: Calendar, color: "#7c3aed" }]),
      { id: "cajas", label: "Cajas", icon: Wallet, color: "#06b6d4" },
      {
        id: "entradas",
        label:
          isCliente && esAdministracion
            ? "Pagos a constructora"
            : "Entradas",
        icon: TrendingUp,
        color: "#10b981",
      },
      { id: "salidas", label: isCliente ? "Gastos" : "Salidas", icon: TrendingDown, color: "#ef4444" },
      { id: "cambios", label: "Cambios", icon: RefreshCw, color: "#6366f1" },
      { id: "cobros", label: "Cobros", icon: DollarSign, color: "#f97316" },
      { id: "comisiones", label: "Comisiones", icon: DollarSign, color: "#0ea5e9" },
      { id: "arqueos", label: "Arqueos", icon: Scale, color: "#14b8a6" },
      {
        id: "proyectos",
        label: "Proyectos",
        icon: Building2,
        color: "#8b5cf6",
      },
      // ✅ NUEVA VISTA: KPI Trabajadores
      {
        id: "kpi-trabajadores",
        label: "KPI Trabajadores",
        icon: Users,
        color: "#8b5cf6",
      },
      // ✅ NUEVA VISTA: Notas del Proyecto
      {
        id: "notas-proyecto",
        label: "Notas del Proyecto",
        icon: FileText,
        color: "#8b5cf6",
      },
      { id: "admin", label: "Admin", icon: Settings, color: "#64748b" },
    ];

    // ✅ NUEVO: Si es super admin, agregar vistas de sistema
    if (esSuperAdmin(usuario)) {
      items.push(
        {
          id: "system-demos",
          label: "Demos Sistema",
          icon: Crown,
          color: "#dc2626",
        },
        {
          id: "system-stats",
          label: "Estadísticas",
          icon: Database,
          color: "#dc2626",
        }
      );
    }

    return items.filter((item) => checkVistaAccess(item.id));
  };

  const menuItems = getAllMenuItems();

  const handleItemClick = (itemId) => {
    console.log(`[SidebarMobile] Cambiando a la vista: ${itemId}`); // LOG AÑADIDO
    setCurrentView(itemId);
    setIsOpen(false);
  };

  const styles = {
    floatingButton: {
      position: "fixed",
      top: "24px",
      left: "20px",
      width: "52px",
      height: "52px",
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      boxShadow:
        "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      zIndex: 1000,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      transform: `translateY(${isVisible ? "0" : "-80px"})`,
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
    },

    floatingButtonHover: {
      transform: "scale(1.05)",
      boxShadow:
        "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)",
    },

    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      zIndex: 1500,
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? "visible" : "hidden",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },

    sidebar: {
      position: "fixed",
      top: 0,
      left: 0,
      bottom: 0,
      width: "320px",
      maxWidth: "85vw",
      backgroundColor: "#ffffff",
      borderTopRightRadius: "24px",
      borderBottomRightRadius: "24px",
      boxShadow:
        "0 25px 80px rgba(0, 0, 0, 0.25), 0 8px 32px rgba(0, 0, 0, 0.12)",
      zIndex: 1600,
      transform: `translateX(${isOpen ? "0" : "-100%"})`,
      transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    sidebarHeader: {
      padding: "32px 24px 24px 24px",
      borderBottom: "1px solid #f1f5f9",
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    },

    headerTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
    },

    closeButton: {
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      backgroundColor: "rgba(100, 116, 139, 0.1)",
      border: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.2s ease",
      color: "#64748b",
    },

    logoContainer: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },

    logoImage: {
      width: "48px",
      height: "48px",
      borderRadius: "14px",
      objectFit: "cover",
      border: "2px solid rgba(255, 255, 255, 0.8)",
      backgroundColor: "#f8fafc",
    },

    logoFallback: {
      width: "48px",
      height: "48px",
      borderRadius: "14px",
      backgroundColor: "#3b82f6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
    },

    companyInfo: {
      flex: 1,
    },

    companyName: {
      fontSize: "1.125rem",
      fontWeight: "700",
      color: "#1e293b",
      margin: "0 0 4px 0",
      lineHeight: "1.3",
    },

    userInfo: {
      fontSize: "0.875rem",
      color: "#64748b",
      margin: 0,
      fontWeight: "500",
    },

    navigation: {
      flex: 1,
      padding: "8px 0",
      overflowY: "auto",
      overflowX: "hidden",
      overscrollBehaviorY: "contain", // <-- Solución al scroll para mobile
    },

    menuItem: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      padding: "16px 24px",
      margin: "0 16px 4px 16px",
      borderRadius: "16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      position: "relative",
      textDecoration: "none",
      border: "none",
      backgroundColor: "transparent",
      width: "calc(100% - 32px)",
      textAlign: "left",
    },

    menuItemActive: {
      backgroundColor: "rgba(59, 130, 246, 0.08)",
      borderLeft: "3px solid #3b82f6",
      paddingLeft: "21px",
    },

    menuItemHover: {
      backgroundColor: "rgba(100, 116, 139, 0.04)",
      transform: "translateX(4px)",
    },

    iconContainer: {
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      flexShrink: 0,
    },

    menuItemText: {
      fontSize: "0.95rem",
      fontWeight: "600",
      flex: 1,
      transition: "color 0.2s ease",
    },

    chevron: {
      transition: "transform 0.2s ease",
      opacity: 0.5,
    },

    sidebarFooter: {
      padding: "16px 24px 32px 24px",
      borderTop: "1px solid #f1f5f9",
    },

    logoutButton: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      padding: "16px 20px",
      backgroundColor: "rgba(239, 68, 68, 0.08)",
      border: "1px solid rgba(239, 68, 68, 0.2)",
      borderRadius: "16px",
      color: "#dc2626",
      fontSize: "0.95rem",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },

    logoutButtonHover: {
      backgroundColor: "rgba(239, 68, 68, 0.12)",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(239, 68, 68, 0.2)",
    },
  };

  return (
    <>
      <button
        style={styles.floatingButton}
        onClick={() => setIsOpen(true)}
        onMouseEnter={(e) => {
          Object.assign(e.target.style, styles.floatingButtonHover);
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = `translateY(${isVisible ? "0" : "-80px"})`;
          e.target.style.boxShadow =
            "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)";
        }}
        aria-label="Abrir menú"
      >
        <Menu size={24} color="#64748b" />
      </button>

      <div style={styles.overlay} onClick={() => setIsOpen(false)} />

      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.headerTop}>
            <div style={styles.logoContainer}>
              {constructora?.logo_url ? (
                <img
                  src={constructora.logo_url}
                  alt="Logo"
                  style={styles.logoImage}
                />
              ) : (
                <div style={styles.logoFallback}>
                  <Building2 size={24} />
                </div>
              )}
              <div style={styles.companyInfo}>
                <h2 style={styles.companyName}>
                  {constructora?.nombre || "Mi Empresa"}
                </h2>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <p style={styles.userInfo}>{usuario?.nombre || "Usuario"}</p>
                  <button
                    onClick={() => setIsConfigModalOpen(true)}
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "4px",
                      background: "transparent",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#64748b",
                      transition: "all 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "#e2e8f0";
                      e.target.style.color = "#3b82f6";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.color = "#64748b";
                    }}
                  >
                    <Settings size={12} />
                  </button>
                </div>
              </div>
            </div>

            <button
              style={styles.closeButton}
              onClick={() => setIsOpen(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "rgba(100, 116, 139, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "rgba(100, 116, 139, 0.1)";
              }}
              aria-label="Cerrar menú"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <nav ref={navRefMobile} style={styles.navigation}>
          {" "}
          {/* Usa la ref aquí para el scroll fix */}
          {menuItems.map((item) => {
            const isActive = currentView === item.id;
            const IconComponent = item.icon;

            return (
              <button
                key={item.id}
                style={{
                  ...styles.menuItem,
                  ...(isActive ? styles.menuItemActive : {}),
                }}
                onClick={() => handleItemClick(item.id)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    Object.assign(e.target.style, styles.menuItemHover);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }
                }}
              >
                <div
                  style={{
                    ...styles.iconContainer,
                    backgroundColor: isActive
                      ? `${item.color}15`
                      : "rgba(100, 116, 139, 0.08)",
                  }}
                >
                  <IconComponent
                    size={20}
                    color={isActive ? item.color : "#64748b"}
                  />
                </div>

                <span
                  style={{
                    ...styles.menuItemText,
                    color: isActive ? item.color : "#1e293b",
                  }}
                >
                  {item.label}
                </span>

                <ChevronRight
                  size={16}
                  style={{
                    ...styles.chevron,
                    transform: isActive ? "translateX(4px)" : "translateX(0)",
                    color: isActive ? item.color : "#94a3b8",
                  }}
                />
              </button>
            );
          })}
        </nav>

        <div style={styles.sidebarFooter}>
          <button
            style={styles.logoutButton}
            onClick={() => {
              setIsOpen(false);
              onLogout();
            }}
            onMouseEnter={(e) => {
              Object.assign(e.target.style, styles.logoutButtonHover);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(239, 68, 68, 0.08)";
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "none";
            }}
          >
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        body {
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }

        nav::-webkit-scrollbar {
          width: 6px;
        }

        nav::-webkit-scrollbar-track {
          background: transparent;
        }

        nav::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.2);
          border-radius: 3px;
        }

        nav::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 116, 139, 0.3);
        }
      `}</style>

      {/* Modal de Configuración de Cuenta */}
      <ModalConfiguracionCuenta
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        usuario={usuario}
        constructora={constructora}
        onUsuarioUpdate={onUsuarioUpdate}
      />
    </>
  );
}

// Configuración de meta viewport (se mantiene)
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// VISTA_COLORS (actualizado con presupuesto-inicial, kpi-trabajadores y system views)
const VISTA_COLORS = {
  dashboard: { gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)" },
  entradas: { gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
  salidas: { gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
  cobros: { gradient: "linear-gradient(135deg, #eab308 0%, #ca8a04 100%)" },
  cambios: { gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
  arqueos: { gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)" },
  cajas: { gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
  proyectos: { gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" },
  "honorarios-mo": {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
  },
  "facturas-comprobantes": {
    gradient: "linear-gradient(135deg, #eab308 0%, #ca8a04 100%)",
  },
  "facturacion": {
    gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
  },
  presupuestos: {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
  },
  "presupuesto-inicial": {
    gradient: "linear-gradient(135deg, #a855f7 0%, #9333ea 100%)",
  },
  "kpi-trabajadores": {
    // ✅ NUEVO: Color para KPI Trabajadores
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
  },
  "notas-proyecto": {
    // ✅ NUEVO: Color para Notas del Proyecto
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
  },
  // ✅ NUEVO: Colores para vistas de sistema
  "system-demos": {
    gradient: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
  },
  "system-stats": {
    gradient: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
  },
  gantt: { gradient: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)" },
  admin: { gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)" },
};

// Componente Sidebar principal (la versión de escritorio)
function Sidebar({
  constructora,
  usuario,
  currentView,
  setCurrentView,
  isMobile, // Este prop controla qué Sidebar se renderiza
  onLogout,
  permisosCliente,
  isCollapsed, // Ahora viene como prop
  onCollapse, // Para manejar el estado de colapsado
  onUsuarioUpdate, // Para actualizar el estado del usuario
}) {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  // Eliminar el useState interno de isCollapsed
  // const [isCollapsed, setIsCollapsed] = useState(false);
  const navRef = useRef(null);
  const navigate = useNavigate();

  // Lógica para el toggle de colapsado
  const handleCollapseToggle = () => {
    if (onCollapse) {
      onCollapse(!isCollapsed);
    }
  };

  // Manejo del scroll para el sidebar de escritorio
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const handleWheel = (e) => {
      const isAtTop = navElement.scrollTop === 0;
      const isAtBottom =
        navElement.scrollHeight - navElement.clientHeight <=
        navElement.scrollTop + 1; // +1 para tolerancia

      if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault(); // Previene el scroll cuando se llega al límite
        e.stopPropagation(); // Evita que el scroll se propague al contenido principal
      }
    };

    navElement.addEventListener("wheel", handleWheel);
    return () => navElement.removeEventListener("wheel", handleWheel);
  }, []);

  const checkVistaAccess = (vistaId) => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const isConstructoraOrEmpleado =
      usuario?.tipo_usuario === "constructora" ||
      usuario?.tipo_usuario === "empleado";

    // ✅ CONTROL ESPECIAL: Configuración de cuenta (todos los usuarios)
    if (vistaId === "configuracion-cuenta") {
      return true;
    }

    // ✅ CONTROL ESPECIAL: KPI Trabajadores
    if (vistaId === "kpi-trabajadores") {
      return puedeVerKPITrabajadores(usuario);
    }

    // ✅ CONTROL ESPECIAL: Vistas de sistema (solo super admins)
    if (vistaId.startsWith("system-")) {
      return esSuperAdmin(usuario);
    }

    if (isConstructoraOrEmpleado) return true;

    if (isCliente) {
      return puedeAccederAVista(permisosCliente, vistaId);
    }
    return false;
  };

  const getMenuItems = () => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const esLlaveEnMano = permisosCliente?.tipoProyecto === "llave_en_mano";
    const esAdministracion =
      permisosCliente?.tipoProyecto === "administracion" ||
      permisosCliente?.tipoProyecto === "administracion_porcentaje";

    // **ORDEN Y ACTUALIZACIÓN DE ID 'presupuesto-inicial' + NUEVO 'kpi-trabajadores'**
    const allItems = [
      { id: "dashboard", label: "Dashboard", icon: BarChart3 },
      // Agregar Caja solo para clientes, justo después del Dashboard
      ...(isCliente ? [{ id: "cajas", label: "Caja", icon: Wallet }] : []),
      {
        id: "honorarios-mo",
        label: isCliente && esLlaveEnMano ? "Resumen de obra" : "Honorarios y MO",
        icon: Briefcase,
      },
      { id: "facturas-comprobantes", label: "Facturas", icon: FileText },
      { id: "presupuestos", label: "Presupuestos", icon: ClipboardList },
      { id: "presupuesto-inicial", label: "Presupuesto Inicial", icon: Target },
      { id: "notas-proyecto", label: "Notas del Proyecto", icon: FileText },
      ...(isCliente ? [] : [{ id: "gantt", label: "Cronograma", icon: Calendar }]),
      ...(isCliente ? [] : [{ id: "cajas", label: "Cajas", icon: Wallet }]),
      ...(isCliente ? [] : [{ id: "flujos-caja", label: "Flujos de caja", icon: Wallet }]),
      {
        id: "entradas",
        label:
          isCliente && esAdministracion
            ? "Pagos a constructora"
            : "Entradas",
        icon: TrendingUp,
      },
      { id: "salidas", label: isCliente ? "Gastos" : "Salidas", icon: TrendingDown },
      { id: "cambios", label: "Cambios", icon: RefreshCw },
      { id: "cobros", label: "Cobros", icon: DollarSign },
      { id: "arqueos", label: "Arqueos", icon: Scale },
      { id: "proyectos", label: "Proyectos", icon: Building2 },
      // ✅ NUEVA VISTA: KPI Trabajadores
      { id: "kpi-trabajadores", label: "KPI Trabajadores", icon: Users },
      { id: "admin", label: "Admin", icon: Settings },
      { id: "facturacion", label: "Facturación", icon: CreditCard },
    ];

    // ✅ NUEVO: Si es super admin, agregar vistas de sistema
    if (esSuperAdmin(usuario)) {
      allItems.push(
        { id: "system-demos", label: "Demos Sistema", icon: Crown },
        { id: "system-stats", label: "Estadísticas", icon: Database },
        { id: "system-admin", label: "Suscripciones & Pagos", icon: Crown }, // ✅ NUEVO

      );
    }

    return allItems.filter((item) => checkVistaAccess(item.id));
  };
  const menuItems = getMenuItems();

  // Si isMobile es true, renderiza la SidebarMobile
  if (isMobile) {
    return (
      <SidebarMobile
        constructora={constructora}
        usuario={usuario}
        currentView={currentView}
        setCurrentView={setCurrentView}
        onLogout={onLogout}
        permisosCliente={permisosCliente}
        onUsuarioUpdate={onUsuarioUpdate}
      />
    );
  }

  // Renderizado del Sidebar de escritorio
  return (
    <aside
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: isCollapsed ? "72px" : "280px",
        height: "100vh",
        background: "#ffffff",
        color: "#334155",
        overflow: "visible", // Permitir que el botón de colapsar salga del flujo
        zIndex: 1000,
        boxShadow: "3px 0px 30px rgba(0, 0, 0, 0.04)",
        borderRight: "1px solid #eef2f6",
        display: "flex",
        flexDirection: "column",
        fontFamily: "sans-serif",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: "0 24px 24px 0",
      }}
    >
      <button
        onClick={handleCollapseToggle}
        style={{
          position: "absolute",
          top: "80px",
          right: "-17px",
          width: "34px",
          height: "34px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          cursor: "pointer",
          transition: "all 0.3s ease",
          zIndex: 1001,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}
        title={isCollapsed ? "Expandir" : "Colapsar"}
      >
        <ChevronsLeft
          size={18}
          style={{
            transform: isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        />
      </button>

      <div
        style={{
          overflow: "hidden", // Contiene el contenido interno para que no se desborde al colapsar
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <div
          style={{
            padding: "24px 12px",
            display: "flex",
            justifyContent: isCollapsed ? "center" : "flex-start",
            alignItems: "center",
            flexShrink: 0, // Evita que se encoja
          }}
        >
          <div
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "16px",
              background: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {constructora?.logo_url ? (
              <img
                src={constructora.logo_url}
                alt="Logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <Building2 size={22} color="#3b82f6" />
            )}
          </div>
          {!isCollapsed && ( // Muestra el texto solo si no está colapsado
            <div
              style={{
                marginLeft: "12px",
                flex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                {constructora?.nombre || "Mi Empresa"}
              </h2>
              <p
                style={{
                  margin: "2px 0 0 0",
                  fontSize: "12px",
                  color: "#64748b",
                  fontWeight: "400",
                }}
              >
                Sistema de Gestión
              </p>
            </div>
          )}
        </div>

        <nav
          ref={navRef} // Asigna la ref para el control de scroll
          style={{
            padding: "8px 12px",
            flex: 1,
            overflowY: "auto", // Habilita el scroll vertical si el contenido es demasiado largo
            overflowX: "hidden", // Oculta el scroll horizontal
            overscrollBehaviorY: "contain", // <-- Solución al scroll para desktop
          }}
        >
          {!isCollapsed && ( // Muestra el título de navegación solo si no está colapsado
            <p
              style={{
                margin: "0 0 8px 12px",
                fontSize: "11px",
                fontWeight: "600",
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {usuario?.tipo_usuario === "cliente"
                ? "Tu Proyecto"
                : "Navegación"}
            </p>
          )}
          {menuItems.map((item) => {
            const isActive = currentView === item.id;
            const isHovered = hoveredItem === item.id;
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  console.log(`[Sidebar] Cambiando a la vista: ${item.id}`);
                  navigate(`/${item.id}`);
                  setCurrentView(item.id);
                }}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isCollapsed ? "center" : "flex-start",
                  gap: isCollapsed ? 0 : "14px",
                  padding: "10px",
                  margin: "0 0 4px 0",
                  background: isActive
                    ? "#f3f4f6" // gris muy suave para el activo
                    : isHovered
                    ? "#f5f5f5"
                    : "transparent",
                  border: "none", // sin borde para todos
                  outline: "none",
                  borderRadius: "12px",
                  color: isActive ? "#18181b" : "#334155",
                  cursor: "pointer",
                  transition: "all 0.25s ease-out",
                  textAlign: "left",
                  boxShadow: isActive
                    ? "0 5px 15px rgba(0, 0, 0, 0.08)"
                    : "none",
                  transform:
                    isHovered && !isActive ? "translateX(3px)" : "none",
                }}
                title={isCollapsed ? item.label : ""}
              >
                <IconComponent size={22} style={{ color: isActive ? "#18181b" : "#64748b" }} />
                {!isCollapsed && <span style={{ fontWeight: isActive ? 600 : 400 }}>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {!isCollapsed && ( // El footer se muestra solo si no está colapsado
          <div
            style={{
              padding: "16px",
              borderTop: "1px solid #eef2f6",
              flexShrink: 0, // Evita que se encoja
            }}
          >
            {usuario?.tipo_usuario === "cliente" && permisosCliente ? (
              <div
                style={{
                  padding: "12px 14px",
                  background: "#eff6ff",
                  borderRadius: "12px",
                  border: "1px solid #dbeafe",
                  marginBottom: "12px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#1e3a8a",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Building2 size={16} />
                  {permisosCliente.proyectoCodigo}
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "11px",
                    color: "#1e40af",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {permisosCliente.proyectoCliente}
                </p>
              </div>
            ) : esSuperAdmin(usuario) ? (
              // ✅ NUEVO: Mostrar badge de super admin
              <div
                style={{
                  padding: "12px 14px",
                  background:
                    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  borderRadius: "12px",
                  marginBottom: "12px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    fontWeight: "700",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Crown size={16} />
                  SUPER ADMIN
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.9)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Sistema NOKK
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px",
                  background: "#f8fafc",
                  borderRadius: "12px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <User size={18} color="white" />
                </div>
                <div
                  style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden" }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#1e293b",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {usuario?.nombre} {usuario?.apellido}
                  </p>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "11px",
                      color: "#64748b",
                      textTransform: "capitalize",
                    }}
                  >
                    {usuario?.tipo_usuario?.replace("_", " ") || "Usuario"}
                  </p>
                </div>
                <button
                  onClick={() => setIsConfigModalOpen(true)}
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    background: "transparent",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#64748b",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#e2e8f0";
                    e.target.style.color = "#3b82f6";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  <Settings size={14} />
                </button>
              </div>
            )}
            <button
              onClick={onLogout}
              onMouseEnter={() => setHoveredItem("logout")}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "10px 14px",
                background:
                  hoveredItem === "logout" ? "#fee2e2" : "transparent",
                border: `1px solid ${
                  hoveredItem === "logout" ? "#fca5a5" : "#eef2f6"
                }`,
                borderRadius: "12px",
                color: "#ef4444",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease-out",
              }}
            >
              <LogOut size={16} />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        /* Estilos específicos para el scrollbar del nav */
        nav::-webkit-scrollbar {
          width: 5px;
        }
        nav::-webkit-scrollbar-track {
          background: transparent;
        }
        nav::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        nav::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        /* Estilos globales (mantienen los que ya tenías) */
        input,
        textarea,
        select {
          font-size: 16px !important;
        }
      `}</style>

      {/* Modal de Configuración de Cuenta */}
      <ModalConfiguracionCuenta
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        usuario={usuario}
        constructora={constructora}
        onUsuarioUpdate={onUsuarioUpdate}
      />
    </aside>
  );
}

export default Sidebar;
